import {
  CATEGORY_RANKING_STAGE_OPTIONS,
  type CategoryRankingStage,
} from '@/api/category-ranking/types';
import type { ManagerGender } from '@/schemas/managerRegisterPairSchema';
import { Gender } from '@/schemas/registerSchema';
import { getCategoryRankingStageLabel } from '@/utils/category-ranking-utils';

/** A row as the grid holds it: every cell is a string the manager can still be typing. */
export interface ImportGridRow {
  /** Stable React key; never sent to the backend. */
  key: string;
  email: string;
  name: string;
  /** Empty string means "not set yet"; anything else is a value the backend accepts. */
  gender: ManagerGender | '';
  /** "YYYY-MM-DD" as the date input produces it. */
  birthdate: string;
  categoryId?: number;
  stage?: CategoryRankingStage;
}

interface CategoryOption {
  id: number;
  name: string;
}

const TEMPLATE_HEADERS = [
  'email',
  'nombre',
  'genero',
  'nacimiento',
  'categoria',
  'instancia',
] as const;

type TemplateHeader = (typeof TEMPLATE_HEADERS)[number];

// `Gender` is a TS enum, so its members — not the bare string literals — are what type-check
// against ManagerGender. It is a value import for the same reason.
const GENDER_BY_CSV: Record<string, ManagerGender> = {
  masculino: Gender.MALE,
  femenino: Gender.FEMALE,
  otro: Gender.OTHER,
  male: Gender.MALE,
  female: Gender.FEMALE,
  other: Gender.OTHER,
};

let nextKey = 0;

export const emptyGridRow = (): ImportGridRow => ({
  key: `row-${nextKey++}`,
  email: '',
  name: '',
  gender: '',
  birthdate: '',
});

/**
 * The backend's `RegisterUserDTO.birthdate` is an OffsetDateTime parsed with
 * ISO_OFFSET_DATE_TIME: sending the grid's bare "1990-04-20" throws InvalidFormatException and
 * 400s the whole batch, so it is widened to midnight UTC here.
 */
export const toBackendBirthdate = (date: string): string | undefined =>
  date ? `${date}T00:00:00Z` : undefined;

const normalize = (value: string): string =>
  value
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

const stageByLabel = new Map<string, CategoryRankingStage>(
  CATEGORY_RANKING_STAGE_OPTIONS.map((stage) => [
    normalize(getCategoryRankingStageLabel(stage)),
    stage,
  ]),
);

/** Excel in es-AR exports with `;`, so the delimiter is whichever wins in the header line. */
const detectDelimiter = (headerLine: string): string =>
  (headerLine.match(/;/g)?.length ?? 0) > (headerLine.match(/,/g)?.length ?? 0)
    ? ';'
    : ',';

/** Minimal quote-aware split: handles "a, b" cells and "" escaping. No multiline cells. */
const splitLine = (line: string, delimiter: string): string[] => {
  const cells: string[] = [];
  let current = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    if (inQuotes) {
      if (char === '"' && line[i + 1] === '"') {
        current += '"';
        i++;
      } else if (char === '"') {
        inQuotes = false;
      } else {
        current += char;
      }
    } else if (char === '"') {
      inQuotes = true;
    } else if (char === delimiter) {
      cells.push(current);
      current = '';
    } else {
      current += char;
    }
  }
  cells.push(current);
  return cells.map((cell) => cell.trim());
};

export const buildImportTemplateCsv = (categories: CategoryOption[]): string => {
  const firstCategory = categories[0]?.name ?? '4ta';
  const secondCategory = categories[1]?.name ?? firstCategory;
  return [
    TEMPLATE_HEADERS.join(','),
    `juan@mail.com,Juan Perez,MASCULINO,1990-04-20,${firstCategory},Campeón`,
    `ana@mail.com,,,,${secondCategory},Semifinal`,
    '',
    '# nombre/genero/nacimiento solo hacen falta si el email no tiene cuenta en GetMatch',
    '# genero: MASCULINO | FEMENINO | OTRO — nacimiento: AAAA-MM-DD',
    `# instancia: ${CATEGORY_RANKING_STAGE_OPTIONS.map(getCategoryRankingStageLabel).join(' | ')}`,
    `# categoria: ${categories.map((category) => category.name).join(' | ')}`,
  ].join('\n');
};

export const downloadImportTemplate = (categories: CategoryOption[]): void => {
  // The leading BOM makes Excel open the file as UTF-8 so the accents survive.
  const blob = new Blob(['\uFEFF' + buildImportTemplateCsv(categories)], {
    type: 'text/csv;charset=utf-8',
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = 'importacion-ranking.csv';
  link.click();
  URL.revokeObjectURL(url);
};

/**
 * Parses the club's CSV into grid rows. Category and stage are matched by accent-insensitive
 * name; an unmatched value leaves the cell empty and adds a file-level error so the manager
 * fixes it in the grid instead of the spreadsheet.
 */
export const parseImportCsv = (
  text: string,
  categories: CategoryOption[],
): { rows: ImportGridRow[]; errors: string[] } => {
  const lines = text
    .replace(/^\uFEFF/, '')
    .split(/\r?\n/)
    .filter((line) => line.trim() !== '' && !line.trimStart().startsWith('#'));
  if (lines.length === 0) {
    return { rows: [], errors: ['El archivo está vacío.'] };
  }

  const delimiter = detectDelimiter(lines[0]);
  const headers = splitLine(lines[0], delimiter).map(normalize);
  const columnIndex = (name: TemplateHeader): number => headers.indexOf(name);
  if (columnIndex('email') === -1 || columnIndex('instancia') === -1) {
    return {
      rows: [],
      errors: [
        'El archivo no tiene las columnas del template (falta email o instancia).',
      ],
    };
  }

  const categoryByName = new Map(
    categories.map((category) => [normalize(category.name), category.id]),
  );
  const errors: string[] = [];
  const rows = lines.slice(1).map((line, lineIndex) => {
    const cells = splitLine(line, delimiter);
    const cell = (name: TemplateHeader): string => {
      const index = columnIndex(name);
      return index === -1 ? '' : (cells[index] ?? '');
    };
    // +2: the header is line 1 and spreadsheets are 1-based, so this is what the manager sees.
    const lineNumber = lineIndex + 2;

    const row = emptyGridRow();
    row.email = cell('email');
    row.name = cell('nombre');
    row.birthdate = cell('nacimiento');

    const rawGender = cell('genero');
    row.gender = GENDER_BY_CSV[normalize(rawGender)] ?? '';
    if (rawGender && !row.gender) {
      errors.push(`Fila ${lineNumber}: género "${rawGender}" desconocido.`);
    }
    if (row.birthdate && !/^\d{4}-\d{2}-\d{2}$/.test(row.birthdate)) {
      errors.push(
        `Fila ${lineNumber}: la fecha de nacimiento "${row.birthdate}" no tiene formato AAAA-MM-DD.`,
      );
      row.birthdate = '';
    }

    const categoryName = cell('categoria');
    if (categoryName) {
      const categoryId = categoryByName.get(normalize(categoryName));
      if (categoryId === undefined) {
        errors.push(
          `Fila ${lineNumber}: categoría "${categoryName}" desconocida.`,
        );
      } else {
        row.categoryId = categoryId;
      }
    }

    const stageName = cell('instancia');
    if (stageName) {
      const stage = stageByLabel.get(normalize(stageName));
      if (stage === undefined) {
        errors.push(`Fila ${lineNumber}: instancia "${stageName}" desconocida.`);
      } else {
        row.stage = stage;
      }
    }

    return row;
  });

  return { rows, errors };
};
